import { useState } from 'react'
import { motion } from 'framer-motion'
import { FaCopy, FaExternalLinkAlt, FaShieldAlt, FaCheckCircle, FaInfoCircle, FaClock } from 'react-icons/fa'

const DemoPage = () => {
  const [copiedField, setCopiedField] = useState(null)
  const demoEmail = 'demo@acuitydemo.com'
  const demoPassword = 'DemoPass123!'

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const features = [
    {
      icon: '📊',
      title: 'Client Management',
      description: 'View sample clients with profiles, contact info, and session history'
    },
    {
      icon: '📝',
      title: 'Session Notes',
      description: 'Explore structured assessments and therapy notes with real-world examples'
    },
    {
      icon: '📅',
      title: 'Appointments',
      description: 'See how the scheduling system works with sample appointments'
    },
    {
      icon: '💰',
      title: 'Invoicing',
      description: 'Review sample invoices and payment tracking features'
    },
    {
      icon: '📈',
      title: 'Dashboard',
      description: 'Check out analytics, key metrics, and practice overview'
    },
    {
      icon: '🛠️',
      title: 'Practice Tools',
      description: 'Explore staff management, documents, and practice settings'
    }
  ]

  const importantNotes = [
    {
      title: 'Sandbox Environment',
      description: 'This is a completely isolated sandbox. Your changes are not saved permanently.',
      icon: <FaShieldAlt className="w-5 h-5" />
    },
    {
      title: 'Auto-Reset Every 30 Minutes',
      description: 'The demo database automatically resets every 30 minutes to a clean state with sample data.',
      icon: <FaClock className="w-5 h-5" />
    },
    {
      title: 'Limited Access',
      description: 'Some operations like subscription changes are disabled to protect the demo environment.',
      icon: <FaInfoCircle className="w-5 h-5" />
    },
    {
      title: 'Realistic Sample Data',
      description: 'All client and appointment data is fictional but realistic for testing purposes.',
      icon: <FaCheckCircle className="w-5 h-5" />
    }
  ]

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-600 via-primary-700 to-primary-900 py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-400 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl opacity-20"></div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
              Try Acuity Practice Free
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Experience the platform risk-free with our interactive demo. No credit card required.
              All changes reset automatically every 30 minutes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Demo Credentials Section */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
              Demo Credentials
            </h2>
            <p className="text-xl text-gray-600 text-center">
              Use these credentials to access the demo environment
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 md:p-12 border-2 border-primary-200 shadow-lg"
          >
            {/* Email Section */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Email Address
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="email"
                  value={demoEmail}
                  readOnly
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg font-mono text-gray-900 focus:outline-none"
                />
                <button
                  onClick={() => copyToClipboard(demoEmail, 'email')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${
                    copiedField === 'email'
                      ? 'bg-green-600 text-white'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  <FaCopy className="w-4 h-4" />
                  {copiedField === 'email' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Password Section */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Password
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="password"
                  value={demoPassword}
                  readOnly
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg font-mono text-gray-900 focus:outline-none"
                />
                <button
                  onClick={() => copyToClipboard(demoPassword, 'password')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${
                    copiedField === 'password'
                      ? 'bg-green-600 text-white'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  <FaCopy className="w-4 h-4" />
                  {copiedField === 'password' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Launch Demo Button */}
            <a
              href="https://acuitypractice.com"
              className="block w-full text-center bg-gradient-to-r from-primary-600 to-accent-600 text-white py-4 rounded-lg font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group"
            >
              <span>Launch Demo</span>
              <FaExternalLinkAlt className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Important Notes Section */}
      <section className="section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600 rounded-full blur-3xl opacity-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-600 rounded-full blur-3xl opacity-10"></div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Important to Know
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Before diving into the demo, here are a few things to understand
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {importantNotes.map((note, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-xl p-6 border border-gray-600 hover:border-primary-500 transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/20"
              >
                <div className="flex items-start gap-4">
                  <div className="text-primary-400 text-2xl flex-shrink-0 mt-1">
                    {note.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {note.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {note.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Can You Explore?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Test drive all these features in the demo environment
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8 border-2 border-primary-100 hover:shadow-lg transition-all duration-300"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                q: 'Why does the demo reset every 30 minutes?',
                a: 'This ensures the demo environment stays clean and consistent for all users. Your changes are stored in an isolated sandbox that automatically resets, so you can explore without worrying about breaking anything.'
              },
              {
                q: 'Can I export data from the demo?',
                a: 'You can view and interact with all data in the demo, but exports are disabled to maintain the integrity of the sandbox environment.'
              },
              {
                q: 'What if I find a bug?',
                a: 'Great question! Please report any bugs you find using our contact form. Include details about what you were doing when you found the issue.'
              },
              {
                q: 'How long can I use the demo?',
                a: 'The demo is available 24/7 and you can access it as many times as you like. Each session is completely fresh with the same sample data.'
              },
              {
                q: 'Can I see real integrations like Google Calendar?',
                a: 'The demo shows the UI and workflow for integrations, but they are not fully functional in the sandbox for security reasons.'
              },
              {
                q: 'Do I need to create an account?',
                a: 'No! Just use the demo credentials provided above. No signup required to explore the platform.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-6 shadow-md border-l-4 border-primary-600"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.q}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {faq.a}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600 rounded-full blur-3xl opacity-15"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-600 rounded-full blur-3xl opacity-15"></div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-12 md:p-16 shadow-2xl max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Ready to Experience It?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Click below to launch the demo environment and start exploring Acuity Practice.
            </p>
            <a href="https://acuitypractice.com" className="btn-primary text-lg inline-flex items-center gap-2 group">
              <span>Launch Demo Now</span>
              <FaExternalLinkAlt className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default DemoPage
